<template>
    <v-dialog :value="value" max-width="620" scrollable @input="$emit('input', $event)">
        <v-card v-if="day">
            <v-card-title class="d-flex align-center">
                <span>{{ title }}</span>
                <v-spacer />
                <v-btn v-if="canAddLeave" small outlined color="primary" class="text-none mr-2" @click="$emit('add-leave', day)">
                    <v-icon small left>mdi-plus</v-icon>Add leave
                </v-btn>
                <v-btn icon small @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-0">
                <div v-for="sec in sections" :key="sec.key" class="ldd-section">
                    <div class="ldd-head" :style="{ color: sec.color }">
                        <v-icon small :color="sec.color" class="mr-1">{{ sec.icon }}</v-icon>
                        {{ sec.label }} ({{ sec.entries.length }})
                    </div>
                    <div v-if="!sec.entries.length" class="text-caption grey--text px-4 pb-2">Nobody</div>
                    <div v-for="e in sec.entries" :key="sec.key + e.request.id">
                        <div class="ldd-row" @click="$emit('open-request', e.request)">
                            <span class="font-weight-bold">{{ userName(e.userId) }}</span>
                            <v-chip x-small label outlined class="ml-2" :color="colorForType(e.request.leave_type_id)">{{ typeName(e.request) }}</v-chip>
                            <span class="text-caption grey--text ml-2">{{ rangeLabel(e.request) }}</span>
                            <span v-if="e.request.is_half_day" class="text-caption grey--text ml-2">half day</span>
                            <v-spacer />
                            <v-btn x-small text class="text-none" @click.stop="$emit('person-note', { userId: e.userId, request: e.request })">
                                <v-icon x-small left>mdi-note-plus-outline</v-icon>Note
                            </v-btn>
                        </div>
                        <div v-if="e.request.note" class="ldd-emp-note">
                            <v-icon x-small class="mr-1">mdi-comment-text-outline</v-icon>
                            <span class="grey--text text--darken-1">Employee note:</span> {{ e.request.note }}
                        </div>
                        <div v-if="sec.key === 'pending'" class="ldd-decide" @click.stop>
                            <v-textarea :value="decisionNotes[e.request.id]" outlined dense rows="2" hide-details
                                placeholder="Add a note for the employee (optional)"
                                @input="$set(decisionNotes, e.request.id, $event)" />
                            <div class="d-flex justify-end mt-2" style="gap: 8px;">
                                <v-btn small outlined color="error" class="text-none" :loading="updatingId === e.request.id"
                                    :disabled="updatingId !== null" @click="decide(e.request, 3)">Decline</v-btn>
                                <v-btn small color="success" dark class="text-none" :loading="updatingId === e.request.id"
                                    :disabled="updatingId !== null" @click="decide(e.request, 2)">Approve</v-btn>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="ldd-section">
                    <div class="ldd-head">
                        <v-icon small class="mr-1">mdi-note-text-outline</v-icon>
                        Notes ({{ notes.length }})
                        <v-spacer />
                        <v-btn x-small outlined color="primary" class="text-none" @click="$emit('company-note')">
                            <v-icon x-small left>mdi-plus</v-icon>Add note for whole {{ scopeLabel }}
                        </v-btn>
                    </div>
                    <div v-if="!notes.length" class="text-caption grey--text px-4 pb-2">No notes for this day</div>
                    <div v-for="n in notes" :key="n.id" class="ldd-note" :class="{ general: !n.user_id }">
                        <v-chip v-if="n.is_private" x-small label outlined color="deep-purple" class="mr-1" title="Added with the leave. Only managers see it, not the employee.">
                            <v-icon x-small left>mdi-eye-off-outline</v-icon>Private
                        </v-chip>
                        <span class="font-weight-bold">{{ n.user_id ? userName(n.user_id) : 'Whole ' + scopeLabel }}:</span>
                        {{ n.body }}
                        <span v-if="n.author" class="text-caption grey--text"> — {{ n.author.name }}</span>
                    </div>
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import { fullName } from './leaveUtils';

// Everything for one day of the month-grid calendar: who is off / WFH / pending and
// all the notes, with shortcuts to open a request or add a note (the panel owns the
// actual note dialog so edits and deletes keep working there).
export default {
    name: 'LeaveDayDialog',
    props: {
        value: { type: Boolean, default: false },
        day: { type: Object, default: null }, // buildDays() entry
        bucket: { type: Object, default: null }, // { leave, wfh, pending }
        notes: { type: Array, default: () => [] },
        users: { type: Array, default: () => [] },
        leaveTypes: { type: Array, default: () => [] },
        colorForType: { type: Function, required: true },
        scopeLabel: { type: String, default: 'company' },
        updatingId: { default: null }, // request id being approved / declined, or null
        canAddLeave: { type: Boolean, default: false },
    },
    data() {
        return { decisionNotes: {} };
    },
    computed: {
        title() {
            return this.day ? this.$date(this.day.key).format('dddd, MMM D, YYYY') : '';
        },
        sections() {
            const b = this.bucket || { leave: [], wfh: [], pending: [] };
            return [
                { key: 'leave', label: 'On leave', icon: 'mdi-beach', color: '#c2185b', entries: b.leave },
                { key: 'wfh', label: 'Work from home', icon: 'mdi-home-account', color: '#2e7d32', entries: b.wfh },
                { key: 'pending', label: 'Pending approval', icon: 'mdi-clock-outline', color: '#ef6c00', entries: b.pending },
            ];
        },
    },
    methods: {
        decide(request, statusId) {
            this.$emit('decide', { request, statusId, note: this.decisionNotes[request.id] || '' });
        },
        rangeLabel(r) {
            const f = (r.from || '').substring(0, 10);
            const t = (r.to || '').substring(0, 10);
            if (!f) return '';
            return f === t ? this.$date(f).format('MMM D') : `${this.$date(f).format('MMM D')} – ${this.$date(t).format('MMM D')}`;
        },
        userName(id) {
            const u = this.users.find((x) => Number(x.id) === Number(id));
            return u ? fullName(u) : 'Unknown';
        },
        typeName(r) {
            const t = this.leaveTypes.find((x) => x.id === r.leave_type_id) || r.leave_type || {};
            return t.name || 'Leave';
        },
    },
};
</script>

<style scoped>
.ldd-section {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    padding-bottom: 6px;
}

.ldd-head {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 700;
    padding: 10px 16px 4px;
}

.ldd-row {
    display: flex;
    align-items: center;
    padding: 4px 16px;
    cursor: pointer;
}

.ldd-row:hover {
    background: rgba(0, 0, 0, 0.03);
}

.ldd-emp-note {
    font-size: 12px;
    font-style: italic;
    padding: 0 16px 4px 16px;
    white-space: pre-line;
}

.ldd-decide {
    padding: 4px 16px 10px;
}

.ldd-note {
    font-size: 13px;
    margin: 4px 16px;
    padding: 4px 8px;
    background: #fffde7;
    border-left: 3px solid #fbc02d;
    white-space: pre-line;
}

.ldd-note.general {
    background: #ffebee;
    border-left-color: #e53935;
}
</style>
